import { useEffect, useState } from 'react';
import { fetchGalleryCategories, fetchGalleryItems } from '@/lib/queries';
import type { GalleryCategoryData } from '@/types/sanity';
import {
  compareGalleryItems,
  fetchLocalGallery,
  toLocalMedia,
  toSanityMedia,
  type MediaItem,
} from './galleryMedia';

export type LoadStatus = 'loading' | 'ready' | 'error';

/**
 * Combines the CMS gallery (Sanity) and the local gallery (public/galleryimg.json) into one
 * list, and separately loads every category that exists in Sanity (not just the ones currently
 * used by an item) — a category page needs that to tell "real category, just empty so far"
 * apart from "this URL doesn't correspond to anything". All three requests are independent:
 * they load in parallel and whichever succeed are shown.
 */
export function useGalleryMedia() {
  const [mediaArray, setMediaArray] = useState<MediaItem[]>([]);
  const [categories, setCategories] = useState<GalleryCategoryData[]>([]);
  const [status, setStatus] = useState<LoadStatus>('loading');

  useEffect(() => {
    const controller = new AbortController();
    const { signal } = controller;

    const loadMedia = async () => {
      const [sanityResult, localResult, categoriesResult] = await Promise.allSettled([
        fetchGalleryItems({ signal }),
        fetchLocalGallery(signal),
        fetchGalleryCategories({ signal }),
      ]);

      if (signal.aborted) return;

      if (sanityResult.status === 'rejected') {
        console.error('Failed to load gallery from Sanity:', sanityResult.reason);
      }
      if (localResult.status === 'rejected') {
        console.error('Failed to load local gallery manifest:', localResult.reason);
      }
      if (categoriesResult.status === 'rejected') {
        console.error('Failed to load gallery categories:', categoriesResult.reason);
      }

      if (sanityResult.status === 'rejected' && localResult.status === 'rejected') {
        setStatus('error');
        return;
      }

      const sanityMedia =
        sanityResult.status === 'fulfilled'
          ? [...sanityResult.value].sort(compareGalleryItems).flatMap(toSanityMedia)
          : [];
      const localMedia = localResult.status === 'fulfilled' ? localResult.value.map(toLocalMedia) : [];

      setMediaArray([...sanityMedia, ...localMedia]);
      setCategories(categoriesResult.status === 'fulfilled' ? categoriesResult.value : []);
      setStatus('ready');
    };

    loadMedia();

    return () => controller.abort();
  }, []);

  return { mediaArray, categories, status };
}
